import { useMutation } from '@tanstack/react-query'
import { QRCodeSVG } from 'qrcode.react'
import { Copy, ShieldCheck } from 'lucide-react'
import { useEffect, useRef, useState, type FormEvent } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'

import { beginMfaEnrolment, confirmMfaEnrolment } from '@/api/auth-api'
import type { SignInEnrolmentResponse } from '@/api/types'
import { useAuth } from '@/auth/use-auth'
import { ProblemAlert } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

import { AuthLayout } from './auth-layout'

const CODE_LENGTH = 6

/** Base32 in groups of four — the shape a person can read aloud or type without losing count. */
const groupSecret = (secret: string) => secret.replace(/(.{4})/g, '$1 ').trim()

/**
 * First-sign-in authenticator enrolment.
 *
 * MFA is mandatory before a session exists, which puts every newly created account in a circle:
 * the ordinary enrolment route needs a bearer token, and the missing factor is what stops one
 * being issued. This screen is the way through — it spends the sign-in challenge to fetch a
 * secret, shows it as a QR code (or raw, for a phone that cannot scan), and activates it by
 * proving a code, which completes sign-in in the same motion.
 *
 * The secret is inert until proven. Closing this page mid-way leaves the account unenrolled and
 * able to start again; it does not lock anyone out.
 */
export function MfaEnrolPage() {
  const auth = useAuth()
  const navigate = useNavigate()
  const search = useSearch({ from: '/mfa/enrol' })
  const [code, setCode] = useState('')
  const [copied, setCopied] = useState<'secret' | 'recovery' | null>(null)

  const challenge = auth.challenge

  const begin = useMutation({ mutationFn: beginMfaEnrolment })

  // The sign-in challenge token is single-use — this must fire exactly once, including under
  // StrictMode's doubled dev effects, hence the ref rather than an empty dependency list alone.
  const startedRef = useRef(false)
  useEffect(() => {
    if (startedRef.current || !challenge) {
      return
    }
    startedRef.current = true
    begin.mutate(challenge.token)
  }, [begin, challenge])

  const enrolment: SignInEnrolmentResponse | undefined = begin.data

  const confirm = useMutation({
    mutationFn: confirmMfaEnrolment,
    onSuccess: (outcome) => {
      auth.applyOutcome(outcome, challenge?.username ?? '')

      if (outcome.outcome === 'password_change_required') {
        void navigate({ to: '/password/change', search })
      } else if (outcome.outcome === 'authenticated') {
        void (search.redirect ? navigate({ href: search.redirect }) : navigate({ to: '/' }))
      }
    },
  })

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!enrolment) {
      return
    }
    confirm.mutate({ challengeToken: enrolment.challengeToken, code })
  }

  const copyText = (text: string, which: 'secret' | 'recovery') => {
    void navigator.clipboard.writeText(text).then(() => setCopied(which))
  }

  return (
    <AuthLayout
      title="Set up your authenticator"
      description={
        challenge?.reason ??
        'This account requires an authenticator app before its first session.'
      }
    >
      {begin.isError ? (
        <div className="space-y-4">
          <ProblemAlert error={begin.error} />
          <Button
            variant="secondary"
            className="w-full"
            onClick={() => {
              auth.clearChallenge()
              void navigate({ to: '/login' })
            }}
          >
            Back to sign in
          </Button>
        </div>
      ) : !enrolment ? (
        <p className="text-sm text-muted">Preparing your authenticator secret…</p>
      ) : (
        <form className="space-y-5" onSubmit={onSubmit} noValidate>
          <div className="space-y-2">
            <p className="text-sm">
              Scan this with Google Authenticator, Microsoft Authenticator, or any TOTP app:
            </p>
            <div className="flex justify-center rounded-md border border-border-subtle bg-white p-4">
              <QRCodeSVG value={enrolment.otpAuthUri} size={168} marginSize={0} />
            </div>
            <div className="flex items-center justify-between gap-2 rounded-md bg-surface-muted px-3 py-2">
              <code className="text-xs tracking-wide break-all">
                {groupSecret(enrolment.secretBase32)}
              </code>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => copyText(enrolment.secretBase32, 'secret')}
              >
                <Copy />
                {copied === 'secret' ? 'Copied' : 'Copy'}
              </Button>
            </div>
            <p className="text-xs text-muted">
              Can't scan? Add the account manually in your app using this setup key.
            </p>
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-sm font-medium">Recovery codes</p>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => copyText(enrolment.recoveryCodes.join('\n'), 'recovery')}
              >
                <Copy />
                {copied === 'recovery' ? 'Copied' : 'Copy all'}
              </Button>
            </div>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 rounded-md bg-surface-muted px-3 py-2">
              {enrolment.recoveryCodes.map((recovery) => (
                <code key={recovery} className="text-xs tabular-nums">
                  {recovery}
                </code>
              ))}
            </div>
            <p className="text-xs text-muted">
              Each signs you in once if the phone is lost. Store them somewhere that isn't the
              phone — they are not shown again.
            </p>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="enrol-code">Code from the app</Label>
            <Input
              id="enrol-code"
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH))}
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={CODE_LENGTH}
              placeholder="000000"
              className="numeric h-11 text-center text-lg tracking-[0.5em] tabular-nums"
              aria-describedby="enrol-help"
            />
            <p id="enrol-help" className="text-xs text-muted">
              Entering a correct code is what activates the authenticator and signs you in.
            </p>
          </div>

          {confirm.isError ? <ProblemAlert error={confirm.error} /> : null}

          <Button
            type="submit"
            variant="primary"
            className="w-full"
            disabled={confirm.isPending || code.length < CODE_LENGTH}
          >
            <ShieldCheck />
            {confirm.isPending ? 'Activating…' : 'Activate and sign in'}
          </Button>

          <Button
            type="button"
            variant="ghost"
            className="w-full"
            onClick={() => {
              auth.clearChallenge()
              void navigate({ to: '/login' })
            }}
          >
            Start again
          </Button>
        </form>
      )}
    </AuthLayout>
  )
}
